export const DEAL_NAME_COLUMN_KEY = "title";

/** Column keys with bespoke Asset-class editors (a sub-asset depends on its asset). */
export const ASSET_CLASS_KEY = "asset_class";
export const SUB_ASSET_CLASS_KEY = "sub_asset_class";
/** Currency is a characteristic, but the backend exposes it under this fixed key rather than the prefix. */
export const CURRENCY_KEY = "currency";
/** Backend prefix for account-characteristic columns — these open the characteristic editor. */
export const CHARACTERISTIC_KEY_PREFIX = "characteristic___";

/**
 * Characteristic columns the backend may expose under a plain (non-prefixed) key. These stay editable
 * characteristic links, matching the pre-rewrite behaviour where Geography / Currency / Vintage were always
 * rendered via CharacteristicCell. Harmless if the backend actually prefixes them (the plain key never appears).
 */
const KNOWN_CHARACTERISTIC_KEYS = new Set([CURRENCY_KEY, "geography", "vintage"]);

export interface ResolvedColumn {
  key: string;
  title: string;
  variant: "link" | "text";
}

/**
 * A characteristic column opens the characteristic editor. Keys are either prefixed (`characteristic___*`)
 * or one of the known plain characteristic keys. Asset / Sub-asset have their own editors and are excluded.
 */
export const isCharacteristicColumn = (key: string): boolean =>
  KNOWN_CHARACTERISTIC_KEYS.has(key) || key.startsWith(CHARACTERISTIC_KEY_PREFIX);

/** Characteristic columns render as links (teal); asset and plain-string columns render as dark text. */
const columnVariant = (key: string): "link" | "text" => (isCharacteristicColumn(key) ? "link" : "text");

/**
 * Titles whose column key is fixed rather than resolved from the definitions — the backend column isn't
 * exposed by `getDealsTableColumnsActions` under a matching title (e.g. Currency uses the "currency" key).
 */
const COLUMN_KEY_OVERRIDES: Record<string, string> = {
  currency: CURRENCY_KEY
};

/**
 * Map the CHECKED column titles (from `dealListViewConfig`) to concrete columns. A title first resolves via
 * a fixed key override, then by matching a definition title from `getDealsTableColumnsActions`. Order follows
 * `titles`; titles that resolve to neither are dropped.
 */
export const resolveColumnsByTitle = (titles: string[], defs: { key: string; title: string }[]): ResolvedColumn[] =>
  titles
    .map((title) => {
      const override = COLUMN_KEY_OVERRIDES[title.toLowerCase()];
      if (override) return { key: override, title, variant: columnVariant(override) };
      const def = defs.find((item) => item.title.toLowerCase() === title.toLowerCase());
      return def ? { key: def.key, title: def.title, variant: columnVariant(def.key) } : null;
    })
    .filter((column): column is ResolvedColumn => column !== null);
